/**
 * Route copy for `src/routes/email` (keys: `routes.email.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  emailAutomationBuilderPage: {
    emailReceived: 'Email received',
    entryPointAnIncomingEmailTriggers: 'Entry point: an incoming email triggers the flow.',
    classifyAi: 'Classify (AI)',
    categorizesPrioritizesAndAnalyzesTheEmai: 'Categorizes, prioritizes and analyzes the email\'s sentiment.',
    condition: 'Condition',
    trueFalseBranchBasedOnAn: 'True/false branch based on an expression.',
    approval: 'Approval',
    putsTheFlowOnHoldFor: 'Puts the flow on hold for a human approval (HIL).',
    delay: 'Delay',
    waitsForANumberOfSeconds: 'Waits for a number of seconds.',
    notification: 'Notification',
    sendsAnInternalNotification: 'Sends an internal notification.',
    reply: 'Reply',
    preparesSendsAReplyDraftBy: 'Prepares/sends a reply (draft by default).',
    move: 'Move',
    movesTheEmailToAFolder: 'Moves the email to a folder.',
    categorize: 'Categorize',
    appliesACategoryToTheEmail: 'Applies a category to the email.',
    trigger: 'Trigger',
    analysis: 'Analysis',
    logic: 'Logic',
    actions: 'Actions',
    human: 'Human',
    mailboxIdOptional: 'Mailbox (ID, optional)',
    allMailboxes: '(all mailboxes)',
    startsTheFlowWhenAnEmail: 'Starts the flow when an email is received. The message becomes the run\'s context.',
    classifiesTheEmailCategoryPrioritySentim: 'Classifies the email (category, priority, sentiment, entities) with AI. No parameters.',
    expression: 'Expression',
    ctxEmailPriorityUrgent: 'ctx[\'email\'][\'priority\'] == "urgent"',
    thenConnectTheEdges: 'Then connect the edges',
    true: 'true',
    false: 'false',
    durationSeconds: 'Duration (seconds)',
    message: 'Message',
    newUrgentEmailFromInputsFrom: 'New urgent email from {inputs[from]}.',
    messageOptional: 'Message (optional)',
    theFlowGoesInto: 'The flow goes into',
    awaitingApproval: 'awaiting approval',
    untilAHumanDecides: 'until a human decides.',
    replyTemplateIdOptional: 'Reply template (ID, optional)',
    idOfAnEmailTemplate: 'ID of an Email template',
    bodyIfNoTemplate: 'Body (if no template)',
    helloThankYouForYourMessage: 'Hello, thank you for your message…',
    draftOnlyDoNotSendAutomatically: 'Draft only (do not send automatically)',
    draftFirstAutomaticSendingOnlyHappens: 'Draft first: automatic sending only happens in autonomous mode + “Automatic” policy.',
    targetFolderId: 'Target folder (ID)',
    folderId: 'Folder ID',
    category: 'Category',
    supportBilling: 'support, billing…',
    edge: 'Edge',
    delete: 'Delete',
    branchCondition: 'Branch (condition)',
    noLabel: '— no label —',
    simpleEdgeNoLabel: 'Simple edge (no label).',
    configuration: 'Configuration',
    selectANodeOrAnEdge: 'Select a node or an edge. Drag a type from the palette to add it.',
    name: 'Name',
    nodeName: 'Node name',
    palette: 'Palette',
    drag: 'drag →',
    readOnlyOnlyDraftsCanBe: 'Read-only: only drafts can be edited.',
    theInputsMustBeAJson: 'The inputs must be a JSON object.',
    couldNotRunIsTheAutomation: 'Could not run (is the automation published?).',
    runTheAutomation: 'Run the automation',
    viewInAiActivity: 'View in AI Activity →',
    cancel: 'Cancel',
    launching: 'Launching…',
    run: 'Run',
    runStarted: 'Run started:',
    inputsJson: 'Inputs (JSON)',
    simulatesTheFlowWithASample: 'Simulates the flow with a sample email (e.g.',
    fromABComSubject: '{"from":"a@b.com","subject":"…"}',
    couldNotSave: 'Could not save.',
    couldNotValidate: 'Could not validate.',
    couldNotPublish: 'Could not publish.',
    deleteTheAutomation: 'Delete the automation',
    archiveTheAutomation: 'Archive the automation',
    permanentlyDeleteName: 'Permanently delete “{{name}}”?',
    nameIsPublishedItWillBe: '“{{name}}” is published: it will be archived.',
    archive: 'Archive',
    couldNotDelete: 'Could not delete.',
    automations: '← Automations',
    automationName: 'Automation name',
    unsavedChanges: 'unsaved changes',
    saving: 'Saving…',
    save: 'Save',
    validate: 'Validate',
    publish: 'Publish',
    publishFirst: 'Publish first',
    run2: 'Run',
    specValid: '✓ Spec valid.',
    lengthErrorS: '{{length}} error(s):',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    fitToScreen: 'Fit to screen',
    backspace: 'Backspace',
    delete2: 'Delete',
    couldNotLoadTheAutomations: 'Could not load the automations.',
    newEmailAutomation: 'New email automation',
    emailTriageReplyAutomation: 'Email triage/reply automation',
    theCreationFailed: 'The creation failed.',
    visualBuilder: 'Visual builder',
    dragAndDropEmailAutomationsTrigger: 'Drag-and-drop email automations — trigger, AI classification, conditions, replies and approvals.',
    creating: 'Creating…',
    newAutomation: '+ New automation',
    noAutomationsCreateOneToTriage: 'No automations. Create one to triage and reply automatically.',
    vNodeS: '{{v}} node(s)',
    open: 'Open →',
    automationNotFound: 'Automation not found.',
    back: '← Back',
    loadingTheEditor: 'Loading the editor…',
    emailAutomations: 'Email automations',
    productEvent: 'Product event',
    startsTheFlowOnAnEvent: 'Starts the flow on an event from your product.',
    lifecycleEmail: 'Lifecycle email',
    emailToTheUserCemOr: 'Email to the user — CEM or transactional, consent verified.',
    lifecycle: 'Lifecycle',
    n1Day: '1 day',
    welcomeInputsPropertiesFirstName: 'Welcome {inputs[properties][first_name]}!',
    pHelloInputsPropertiesFirstName: '<p>Hello {inputs[properties][first_name]},</p><p>Here is how to get started…</p>',
    signupD1: '(signup → D+1)',
  },
  emailHubPage: {
    mailboxes: 'Mailboxes',
    inbox: 'Inbox',
    rulesAutomation: 'Rules & automation',
    visualBuilder: 'Visual builder',
    models: 'Models',
    email: 'Email',
    connectedMailboxesAiClassifiedInboxTriag: 'Connected mailboxes, AI-classified inbox, triage rules and automations (draft first, explicit sending).',
    emailSections: 'Email sections',
  },
  emailRulesPage: {
    statusOn: 'ON',
    statusOff: 'OFF',
    suggest: 'Suggest',
    draft: 'Draft',
    automatic: 'Automatic',
    draftOnly: 'Draft only',
    autonomous: 'Autonomous',
    classifyAi: 'Classify (AI)',
    setTheCategory: 'Set the category',
    setThePriority: 'Set the priority',
    moveToAFolder: 'Move to a folder',
    addTags: 'Add tags',
    assignToAnAgent: 'Assign to an agent',
    replyTemplate: 'Reply (template)',
    couldNotLoadTheAutomationPolicy: 'Could not load the automation policy.',
    policySaved: 'Policy saved.',
    automaticModeRequiresAcknowledgingTheLaw: '“Automatic” mode requires acknowledging the Law 25 notice below before it can be enabled.',
    savingThePolicyFailed: 'Saving the policy failed.',
    emergencyStop: 'Emergency stop',
    immediatelyDisableAutomationAndPauseEver: 'Immediately disable automation AND pause every active rule? No automatic action will run until it is re-enabled.',
    stopEverything: 'Stop everything',
    automationStoppedVRuleSPaused: 'Automation stopped. {{v}} rule(s) paused.',
    theEmergencyStopFailed: 'The emergency stop failed.',
    automationPolicy: 'Automation policy',
    automationsAreOffByDefaultRaise: 'Automations are off by default. Raise autonomy step by step.',
    emergencyStop2: '⛔ Emergency stop',
    automationEnabled: 'Automation enabled',
    modeAutonomy: 'Mode (autonomy)',
    minimumConfidence: 'Minimum confidence',
    threshold01BelowWhichThe: 'Threshold (0–1) below which the AI never acts on its own.',
    dailyCap: 'Daily cap',
    n0Unlimited: '0 = unlimited.',
    quietHoursStart: 'Quiet hours — start',
    quietHoursEnd: 'Quiet hours — end',
    law25NoticeAutomation: 'Law 25 notice — automation',
    automaticModeLetsTheAiSend: '“Automatic” mode lets the AI send replies and file emails without human approval. Under Law 25, you acknowledge informing the people concerned about automated processing and keeping a record of decisions (contestable / reversible below).',
    iAcknowledgeTheLaw25Notice: 'I acknowledge the Law 25 notice and consent to automated processing.',
    acknowledgedOn: 'Acknowledged on',
    saveTheAcknowledgment: 'Save the acknowledgment',
    couldNotLoadTheRules: 'Could not load the rules.',
    deleteTheRule: 'Delete the rule',
    deleteNameThisCannotBeUndone: 'Delete “{{name}}”? This cannot be undone.',
    delete: 'Delete',
    ruleDeleted: 'Rule deleted.',
    deletionFailed: 'Deletion failed.',
    theUpdateFailed: 'The update failed.',
    triageRules: 'Triage rules',
    conditionsOnIncomingEmailsOrderedActions: 'Conditions on incoming emails → ordered actions (classify, move, reply…).',
    newRule: '+ New rule',
    noRulesCreateOneToTriage: 'No rules. Create one to triage your emails automatically.',
    active: 'Active',
    disabled: 'Disabled',
    stopOnMatch: 'Stop on match',
    actionS: 'action(s):',
    disable: 'Disable',
    enable: 'Enable',
    edit: 'Edit',
    newRule2: 'New rule',
    editTheRule: 'Edit the rule',
    createRule: 'Create rule',
    save: 'Save',
    theNameIsRequired: 'The name is required.',
    addAtLeastOneAction: 'Add at least one action.',
    savingFailed: 'Saving failed.',
    name: 'Name',
    eGSupportRequests: 'E.g. Support requests',
    trigger: 'Trigger',
    emailReceived: 'Email received',
    emailClassified: 'Email classified',
    conditions: 'Conditions',
    senderContains: 'Sender contains',
    clientCom: '@client.com',
    subjectContains: 'Subject contains',
    invoice: 'invoice',
    keywordsCommaSeparated: 'Keywords (comma-separated)',
    urgentRefundCancellation: 'urgent, refund, cancellation',
    regularExpressionOptional: 'Regular expression (optional)',
    factureInvoiceSD: '(facture|invoice)\\\\s+#\\\\d+',
    category: 'Category',
    any: '(any)',
    priority: 'Priority',
    matching: 'Matching',
    allAnd: 'All (AND)',
    atLeastOneOr: 'At least one (OR)',
    mailboxIdOptional: 'Mailbox (ID, optional)',
    allMailboxes: '(all mailboxes)',
    actions: 'Actions',
    actionTypeV: 'Action type {{v}}',
    remove: 'Remove',
    addAnAction: '+ Add an action',
    autonomy: 'Autonomy',
    priorityOrder: 'Priority order',
    stopIfMatched: 'Stop if matched',
    ruleActive: 'Rule active',
    autonomousModeThisRuleActsWithout: 'Autonomous mode: this rule acts without human approval only if the global policy is in “Automatic” mode (Law 25 notice acknowledged).',
    cancel: 'Cancel',
    categoryToApply: 'Category to apply',
    priorityToApply: 'Priority to apply',
    priority2: 'Priority…',
    targetFolder: 'Target folder',
    folder: 'Folder…',
    tagsToAdd: 'Tags to add',
    tagsCommaSeparated: 'Tags (comma-separated)',
    assignedAgent: 'Assigned agent',
    userIdentifier: 'User identifier',
    replyTemplate2: 'Reply template',
    replyTemplate3: 'Reply template…',
    theReplyIsPreparedAsA: 'The reply is prepared as a draft (automatic sending only in autonomous mode + “Automatic” policy).',
    letsTheAiDetermineCategoryPriority: 'Lets the AI determine category, priority and sentiment. No parameters required.',
    creatingTheFolderFailed: 'Creating the folder failed.',
    deleteTheFolder: 'Delete the folder',
    deleteName: 'Delete “{{name}}”?',
    folders: 'Folders',
    newFolder: 'New folder',
    add: 'Add',
    noFolders: 'No folders.',
    triageCategories: 'Triage categories',
    predefinedTaxonomyYourCustomSlugsTo: 'Predefined taxonomy + your custom slugs. To add one, use the “Set the category” action in a rule — it will appear here.',
    noCategories: 'No categories.',
    priorityPriority: 'priority: {{priority}}',
    predefined: 'predefined',
    custom: 'custom',
    couldNotLoadTheDecisionLog: 'Could not load the decision log.',
    whyIsThisDecisionWrongOptional: 'Why is this decision wrong? (optional)',
    decisionContestedFeedbackSaved: 'Decision contested (feedback saved).',
    theContestFailed: 'The contest failed.',
    undoTheDecision: 'Undo the decision',
    undoThisAutomatedActionWhereReversible: 'Undo this automated action where reversible?',
    undoTheAction: 'Undo the action',
    decisionUndone: 'Decision undone.',
    theUndoFailed: 'The undo failed.',
    decisionLog: 'Decision log',
    everyAutomatedActionIsTracedLaw: 'Every automated action is traced (Law 25) — contestable and reversible.',
    noAutomatedDecisionYet: 'No automated decision yet.',
    confidenceV: 'confidence {{v}}%',
    contestedUndone: 'Contested / undone',
    contest: 'Contest',
    low: 'Low',
    normal: 'Normal',
    high: 'High',
    urgent: 'Urgent',
  },
  emailTemplatesPage: {
    couldNotLoadTheTemplates: 'Could not load the templates.',
    deleteTheTemplate: 'Delete the template',
    deleteNameThisCannotBeUndone: 'Delete “{{name}}”? This cannot be undone.',
    delete: 'Delete',
    templateDeleted: 'Template deleted.',
    deletionFailed: 'Deletion failed.',
    emailTemplates: 'Email templates',
    reusedInTheReplyComposerAnd: 'Reused in the reply composer and the rules\' “Reply” action.',
    newTemplate: '+ New template',
    noTemplatesCreateOneToReply: 'No templates. Create one to reply faster.',
    subjectSubject: 'Subject: {{subject}}',
    preview: 'Preview',
    edit: 'Edit',
    newTemplate2: 'New template',
    editTheTemplate: 'Edit the template',
    create: 'Create',
    save: 'Save',
    templateSaved: 'Template saved.',
    nameSubjectAndHtmlBodyAre: 'Name, subject and HTML body are required.',
    savingFailed: 'Saving failed.',
    name: 'Name',
    acknowledgmentOfReceipt: 'Acknowledgment of receipt',
    categoryOptional: 'Category (optional)',
    support: 'support',
    subject: 'Subject',
    nomVariablesAreRenderedInThe: '{{nom}} variables are rendered in the preview / on send.',
    helloFirstName: 'Hello {{first_name}}',
    htmlBody: 'HTML body',
    pHelloFirstNameP: '<p>Hello {{first_name}},</p>',
    textBodyOptional: 'Text body (optional)',
    helloFirstName2: 'Hello {{first_name}},',
    cancel: 'Cancel',
    thePreviewFailed: 'The preview failed.',
    previewName: 'Preview — {{name}}',
    variables: 'Variables',
    rendering: 'Rendering…',
    renderThePreview: 'Render the preview',
    body: 'Body',
  },
  mailInboxPage: {
    unread: 'Unread',
    read: 'Read',
    replied: 'Replied',
    archived: 'Archived',
    snoozed: 'Snoozed',
    justNow: 'just now',
    minMinAgo: '{{min}} min ago',
    hHAgo: '{{h}} h ago',
    couldNotLoadTheInbox: 'Could not load the inbox.',
    noFoldersCreateAFolderFirst: 'No folders — create a folder first.',
    noUnfiledMessageToOrganize: 'No unfiled message to organize.',
    filedMessageSFiledV: '{{filed}} message(s) filed{{v}}',
    unfiledLeftUnfiled: ', {{unfiled}} left unfiled',
    deterministicRuleAiUnavailable: ' — deterministic rule (AI unavailable)',
    automaticOrganizationFailed: 'Automatic organization failed.',
    filterByMailbox: 'Filter by mailbox',
    allMailboxes: 'All mailboxes',
    filterByFolder: 'Filter by folder',
    allFolders: 'All folders',
    filterByCategory: 'Filter by category',
    allCategories: 'All categories',
    filterByPriority: 'Filter by priority',
    allPriorities: 'All priorities',
    filterByStatus: 'Filter by status',
    allStatuses: 'All statuses',
    search: 'Search…',
    searchTheMailbox: 'Search the mailbox',
    resetTheFilters: 'Reset the filters',
    automaticallyFileUnfiledMessagesIntoExis: 'Automatically file unfiled messages into existing folders (nothing is sent)',
    organizing: 'Organizing…',
    organizeTheInboxWithAi: '✨ Organize the inbox with AI',
    loading: 'Loading…',
    noEmailMatchesConnectAMailbox: 'No email matches. Connect a mailbox or adjust the filters.',
    noSubject: '(no subject)',
    messageS: 'message(s)',
    messageNotFound: 'Message not found.',
    classificationUpdated: 'Classification updated.',
    reclassificationFailed: 'Reclassification failed.',
    movedToV: 'Moved to “{{v}}”.',
    removedFromTheFolder: 'Removed from the folder.',
    theMoveFailed: 'The move failed.',
    thisEmailSAttachmentsAreIn: 'This email\'s attachments are in the Documents hub.',
    selectAnEmailToReadIt: 'Select an email to read it.',
    loadingTheMessage: 'Loading the message…',
    sentimentSentiment: 'Sentiment: {{sentiment}}',
    intentIntent: 'Intent: {{intent}}',
    emailThisMessageSAttachmentsFiled: 'Email this message\'s attachments (filed in the Documents hub)',
    searching: 'Searching…',
    forwardTheAttachments: '📎 Forward the attachments',
    reclassify: 'Reclassify',
    category: 'Category',
    category2: 'Category…',
    priority: 'Priority',
    priority2: 'Priority…',
    rerunTheAutomaticClassification: 'Rerun the automatic classification',
    reclassifyWithAi: '✨ Reclassify with AI',
    folder: 'Folder',
    moveToAFolder: 'Move to a folder',
    noFolder: 'No folder',
    fileThisMessageIntoTheBest: 'File this message into the best existing folder (nothing is sent)',
    organizeWithAi: '✨ Organize with AI',
    currentFolder: 'Current folder:',
    emptyMessage: '(Empty message)',
    emailSentFromFromEmail: 'Email sent from {{from_email}}.',
    emailSent: 'Email sent.',
    replySent: 'Reply sent.',
    sendingFailed: 'Sending failed.',
    draftSaved: 'Draft saved.',
    savingTheDraftFailed: 'Saving the draft failed.',
    model: 'model',
    noDraftGenerated: 'No draft generated.',
    draftSuggestedTemplateAiUnavailable: 'Draft suggested (template — AI unavailable).',
    lengthAiDraftSSuggested: '{{length}} AI draft(s) suggested.',
    couldNotGenerateTheDraft: 'Could not generate the draft.',
    reply: 'Reply',
    draftFirstNothingIsSentUntil: 'Draft first — nothing is sent until you click “Send”.',
    insertATemplate: 'Insert a template',
    noTemplateAvailable: 'No template available',
    chooseATemplate: '— choose a template —',
    selectAnEmailToReply: 'Select an email to reply.',
    writeYourReply: 'Write your reply…',
    replyBody: 'Reply body',
    dictate: 'Dictate',
    aiDraftsClickToInsert: 'AI drafts — click to insert',
    suggestAiRepliesNothingIsSent: 'Suggest AI replies (nothing is sent automatically)',
    aiWriting: 'AI writing…',
    aiDraft: '✨ AI draft',
    saveDraft: 'Save draft',
    sending: 'Sending…',
    send: 'Send',
    low: 'Low',
    normal: 'Normal',
    high: 'High',
    urgent: 'Urgent',
    ai: 'AI',
  },
  mailboxesPage: {
    googleWorkspaceGmailViaOauthOr: 'Google Workspace / Gmail via OAuth or an app password.',
    microsoft365OutlookViaOauthOr: 'Microsoft 365 / Outlook via OAuth or an app password.',
    yahooMailViaAnImapApp: 'Yahoo Mail via an IMAP app password.',
    otherImapSmtp: 'Other (IMAP/SMTP)',
    anyImapSmtpProviderHostPort: 'Any IMAP/SMTP provider: host, port, credentials.',
    connected: 'Connected',
    syncing: 'Syncing…',
    error: 'Error',
    disconnected: 'Disconnected',
    pending: 'Pending',
    couldNotLoadTheConnectedMailboxes: 'Could not load the connected mailboxes.',
    oauthAuthorizationFailedOrWasDenied: 'OAuth authorization failed or was denied. Try again.',
    mailboxConnected: 'Mailbox connected.',
    connectionAuthorizedButNotFoundTry: 'Connection authorized but not found. Try connecting the mailbox again.',
    finalizingTheConnectionFailed: 'Finalizing the connection failed.',
    connectedMailboxes: 'Connected mailboxes',
    connectYourInboxesForTwoWay: 'Connect your inboxes for two-way sync and AI triage.',
    connectAMailbox: '+ Connect a mailbox',
    noMailboxConnectedConnectAGmail: 'No mailbox connected. Connect a Gmail, Outlook, Yahoo or IMAP mailbox to start receiving and triaging your emails.',
    connectAMailbox2: 'Connect a mailbox',
    disconnectTheMailbox: 'Disconnect the mailbox',
    disconnectEmailAddressSyncingWillStop: 'Disconnect “{{email_address}}”? Syncing will stop.',
    disconnect: 'Disconnect',
    mailboxDisconnected: 'Mailbox disconnected.',
    disconnectingFailed: 'Disconnecting failed.',
    lastSyncV: 'Last sync: {{v}}',
    messagesSyncedMessageS: ' · {{messages_synced}} message(s)',
    connectionVerified: '● Connection verified',
    failedV: '● Failed: {{v}}',
    testing: 'Testing…',
    testTheConnection: 'Test the connection',
    connectLabel: 'Connect — {{label}}',
    emailAddressAndAppPasswordAre: 'Email address and app password are required.',
    theImapHostIsRequiredFor: 'The IMAP host is required for a custom provider.',
    theConnectionFailed: 'The connection failed.',
    thisProviderDoesNotSupportOauth: 'This provider does not support OAuth.',
    oauthRecommended: 'OAuth (recommended)',
    appPassword: 'App password',
    oauthAppNotConfigured: 'OAuth app not configured',
    theLabelOauthAppIsNot: 'The {{label}} OAuth app is not configured on the server yet. Use an app password in the meantime, or ask an administrator to configure the OAuth app.',
    useAnAppPassword: 'Use an app password',
    youWillBeRedirectedToLabel: 'You will be redirected to {{label}} to authorize access. No password is stored.',
    emailAddressOptional: 'Email address (optional)',
    youExampleCom: 'you@example.com',
    emailAddress: 'Email address',
    generatedInYourProviderSSecurity: 'Generated in your provider\'s security settings (not your main password).',
    imapHost: 'IMAP host',
    imapExampleCom: 'imap.example.com',
    imapPort: 'IMAP port',
    smtpHostOptional: 'SMTP host (optional)',
    smtpExampleCom: 'smtp.example.com',
    smtpPortOptional: 'SMTP port (optional)',
    back: '← Back',
    connecting: 'Connecting…',
    authorize: 'Authorize →',
    connect: 'Connect',
    unverified: 'unverified',
  },
};

export default en;
